import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { Category, ShortcutKey } from "@modules/app/v2/shortcut-types.ts";
import { computed, defineAsyncComponent, nextTick } from "vue";
import { useStore } from "vuex";
import {
  CATEGORIES,
  NonTeacherNonStudentShortcuts,
  SHORTCUT_ITEM_MAP,
  SHORTCUT_KEYS,
  SHORTCUT_ROUTE_QUERY_MAP,
  StudentShortcuts,
  TeacherShortcuts,
} from "@modules/app/v2/shortcut-constant.ts";
import { useI18n } from "vue-i18n";
import { INTEGRATOR_APP_FEATURE_CATEGORY } from "@modules/app/service/app.constant.ts";
import { RouteLocationRaw, useRoute, useRouter } from "vue-router";
import { isDefined } from "@utils/var.check.ts";
import { RESOURCE_DOMAIN_PATH } from "@plugins/common/constant/resource.constant.ts";
import { FROM_LOCATION_TYPE } from "@modules/assignment/service/assignment.constant.ts";
import { NOTIFICATION_SCOPE } from "@modules/notification/service/notification.constant.ts";
import NotificationService from "@modules/notification/service/notification.service.ts";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import NotificationApi from "@apis/notification.api.ts";
import { DATA_STATE } from "@plugins/modal/modal.constant.ts";
import { TAB } from "@/modules/student-group/service/student.group.constant";
import { useSwitchAccount } from "@modules/app/v2/composables/switch-acccount.ts";
import { useMyDriveAddFromLibrary } from "@modules/my-drive/composables/mydrive-add-from-library.ts";

export function useShortcut() {
  const store = useStore();
  const router = useRouter();
  const route = useRoute();
  const toast = useToast();
  const { t } = useI18n();
  const contentSubpage = useContentSubpageInstance();
  const { switchAccount, showAccountChangeOption } = useSwitchAccount();
  const { onSelect } = useMyDriveAddFromLibrary();

  const isTeacher = computed(() => store.getters["auth/isTeacher"]);
  const isStudent = computed(() => store.getters["auth/isStudent"]);
  const isTeacherAssociatedStudent = computed(
    () => store.getters["auth/isTeacherAssociatedStudent"],
  );
  const isUserLimitedAccess = computed(() => store.getters["auth/isUserLimitedAccess"]);

  const hasAppPermission = (shortcut: ShortcutKey): boolean => {
    const shortcutItem = SHORTCUT_ITEM_MAP[shortcut];

    if (!shortcutItem) {
      return false;
    }

    if (!shortcutItem.isAppPermissionRequired) {
      return true;
    }

    // Check if shortcut is in INTEGRATOR_APP_FEATURE_CATEGORY list
    if (!Object.keys(INTEGRATOR_APP_FEATURE_CATEGORY).includes(shortcut)) {
      return false; // No need for further permission checks if not in the list
    }

    return (
      store.getters["app/hasIntegratorAppCategoryAccess"](shortcut) &&
      store.getters["app/isIntegratorAppEnabled"](shortcut)
    );
  };

  const hasAccessPermission = (shortcut: ShortcutKey): boolean => {
    return !isUserLimitedAccess.value || shortcut !== SHORTCUT_KEYS.SWITCH_TO_STUDENT_ACCOUNT;
  };

  const isShortcutAccessible = (shortcut: ShortcutKey): boolean => {
    return hasAppPermission(shortcut) && hasAccessPermission(shortcut);
  };

  const getAvailableShortcuts = (shortcuts: ShortcutKey[] = []) => {
    const availableShortcuts = getShortcutsByRole();

    // If no specific shortcuts are provided, return those that have permission
    if (shortcuts.length === 0) {
      return availableShortcuts.filter(isShortcutAccessible);
    }

    // Filter provided shortcuts based on available ones and permission
    return shortcuts.filter(
      (shortcut) => availableShortcuts.includes(shortcut) && isShortcutAccessible(shortcut),
    );
  };

  const getShortcutsByRole = (): ShortcutKey[] => {
    if (isTeacher.value) {
      return TeacherShortcuts;
    }

    if (isTeacherAssociatedStudent.value) {
      return [...StudentShortcuts, SHORTCUT_KEYS.SWITCH_TO_TEACHER_ACCOUNT];
    }

    if (isStudent.value) {
      return StudentShortcuts;
    }

    return NonTeacherNonStudentShortcuts;
  };

  const getDisplayedShortcutKeysByCategory = (category: Category) => {
    return getAvailableShortcuts().filter((key) =>
      SHORTCUT_ITEM_MAP[key].category.includes(category),
    ) as ShortcutKey[];
  };

  const getShortcutItems = (shortcuts: ShortcutKey[] = []) => {
    return getAvailableShortcuts(shortcuts).map((key) => ({
      key,
      ...SHORTCUT_ITEM_MAP[key],
    }));
  };

  const navigateToContentMastery = (data: { subjectId: string; levelId: string }) => {
    const query = {
      subject: data.subjectId,
      level: data.levelId,
    };

    if (route.name === "ContentMastery") {
      const resolvedRoute = router.resolve({
        name: "ContentMastery",
        query,
      });
      window.location.href = resolvedRoute.href;
    } else {
      router.push({
        name: "ContentMastery",
        query,
      });
    }
  };

  const handleCreateAnnouncement = async (data, subpage) => {
    try {
      const response = await NotificationApi.saveNotification(data);

      if (response.code === "error") {
        subpage.setDataState(DATA_STATE.ERROR);
        subpage.setErrorMessage(t("announcement_error_save"));
        return;
      }

      if (response.code === "ok") {
        subpage.setDataState(DATA_STATE.COMPLETED);
        subpage.actionHidden();
        toast.show("success", {
          title: t("announcement_create_success_toast_message"),
        });
      }
    } catch (error) {
      subpage.setDataState(DATA_STATE.ERROR);
      console.error("Failed to save notification:", error);
    }
  };

  //logic extracted from NotificationList.vue
  const navigateToNotification = () => {
    let notificationType: string[];
    if (isTeacherAssociatedStudent.value) {
      notificationType = ["CLASS_GROUP_ANNOUNCEMENT"];
    } else {
      notificationType = [
        "CLASS_GROUP_ANNOUNCEMENT",
        "COMMUNITY_ANNOUNCEMENT",
        "SYSTEM_ANNOUNCEMENT",
      ];
    }
    const queryData = {
      name: "Notification",
      query: { type: notificationType },
    };

    if (route.name === "Notification") {
      // page reload within notification page
      const resolvedRoute = router.resolve(queryData);
      window.location.href = resolvedRoute.href;
    } else {
      // page navigation to notification page
      router.push(queryData);
    }
  };

  interface EventBinding {
    click: () => void;
  }

  const getEventBinding = (
    shortcutKey: ShortcutKey,
    callback?: () => void,
  ): EventBinding | null => {
    switch (shortcutKey) {
      case SHORTCUT_KEYS.CREATE_OR_EDIT_MODULE: {
        return {
          click: (e?: Event) => {
            handleEventDefaultAndCallbacks(callback, e);
            // Schedule removal
            contentSubpage.removeAll();
            // Executes AFTER removal is done
            nextTick(() => {
              const lessonTemplateContentSubpage = defineAsyncComponent(
                () => import("@modules/editor/components/LessonTemplateContentSubpage.vue"),
              );
              contentSubpage.show(
                lessonTemplateContentSubpage,
                {
                  resourceDomain: RESOURCE_DOMAIN_PATH.MY_LIBRARY,
                  fromLocation: FROM_LOCATION_TYPE.HOME,
                  directCreation: false,
                  isCreatingNewLesson: true,
                },
                {
                  addFromLibrarySelected: onSelect,
                },
              );
            });
          },
        };
      }
      case SHORTCUT_KEYS.COMMUNITY_GALLERY:
        return {
          click: (e?: Event) => {
            handleEventDefaultAndCallbacks(callback, e);
            router.push({
              name: "FindCommunityGallery",
            });
          },
        };
      case SHORTCUT_KEYS.MOE_LIBRARY:
        return {
          click: (e?: Event) => {
            handleEventDefaultAndCallbacks(callback, e);
            router.push({
              name: "FindMoeLibrary",
            });
          },
        };
      case SHORTCUT_KEYS.APP_LIBRARY: {
        return {
          click: (e?: Event) => {
            handleEventDefaultAndCallbacks(callback, e);
            router.push({ name: "AppLibrary" });
          },
        };
      }

      case SHORTCUT_KEYS.EXTERNAL_RESOURCES: {
        return {
          click: (e?: Event) => {
            handleEventDefaultAndCallbacks(callback, e);
            router.push({ name: "ExternalResource" });
          },
        };
      }
      case SHORTCUT_KEYS.FIND_RESOURCES: {
        return {
          click: (e?: Event) => {
            handleEventDefaultAndCallbacks(callback, e);
            router.push({
              name: "FindResource",
            });
          },
        };
      }
      case SHORTCUT_KEYS.CREATE_ANNOUNCEMENT: {
        return {
          click: (e?: Event) => {
            handleEventDefaultAndCallbacks(callback, e);
            // Schedule removal
            contentSubpage.removeAll();
            // Executes AFTER removal is done
            nextTick(() => {
              //logic extracted from CreateAnnouncementQuickActionItem.vue
              NotificationService.showNotificationCreate({
                params: {
                  baseTitle: [t("announcement_new_class_title")],
                  kind: "editor",
                  scope: NOTIFICATION_SCOPE.GROUP,
                  canSubmit: true,
                },
                listeners: {
                  create: handleCreateAnnouncement,
                },
              });
            });
          },
        };
      }
      case SHORTCUT_KEYS.LEARNING_PROGRESS: {
        return {
          click: (e?: Event) => {
            handleEventDefaultAndCallbacks(callback, e);
            // Schedule removal
            contentSubpage.removeAll();
            // Executes AFTER removal is done
            nextTick(() => {
              //logic extracted from AppSideNav.vue
              const mySubjectsContentSubpage = defineAsyncComponent(
                () => import("@plugins/my-subjects/components/MySubjectsContentSubpage.vue"),
              );
              contentSubpage.show(
                mySubjectsContentSubpage,
                {},
                {
                  cardSelected: navigateToContentMastery,
                },
              );
            });
          },
        };
      }
      case SHORTCUT_KEYS.NOTIFICATIONS: {
        return {
          click: (e?: Event) => {
            handleEventDefaultAndCallbacks(callback, e);
            navigateToNotification();
          },
        };
      }
      case SHORTCUT_KEYS.SWITCH_TO_STUDENT_ACCOUNT: {
        return {
          click: (e?: Event) => {
            handleEventDefaultAndCallbacks(callback, e);
            switchAccount().catch(() => {
              return;
            });
          },
        };
      }
      case SHORTCUT_KEYS.SWITCH_TO_TEACHER_ACCOUNT: {
        return {
          click: (e?: Event) => {
            handleEventDefaultAndCallbacks(callback, e);
            showAccountChangeOption().catch(() => {
              return;
            });
          },
        };
      }
      case SHORTCUT_KEYS.PROFESSIONAL_DEVELOPMENT_GROUPS:
      case SHORTCUT_KEYS.STAFF_GROUPS:
      case SHORTCUT_KEYS.STUDENT_GROUPS: {
        return {
          click: (e?: Event) => {
            callback?.();
            //when coming from side nav view menu subpage, e is undefined
            if (isDefined(e)) {
              e?.preventDefault();
            }

            const query = isTeacher.value ? SHORTCUT_ROUTE_QUERY_MAP[shortcutKey] : {};

            router.push({
              name: "StudentGroupListing",
              query: {
                ...query,
                tab: TAB.CURRENT,
              },
            });
          },
        };
      }
      default:
        return null;
    }
  };

  interface LinkBinding {
    onClick?: (e?: Event) => void;
    to?: RouteLocationRaw;
    href?: string;
  }

  const getLinkBinding = (shortcutKey: ShortcutKey): LinkBinding | null => {
    switch (shortcutKey) {
      case SHORTCUT_KEYS.MY_DRIVE: {
        return {
          to: {
            name: "MyDriveHome",
          },
        };
      }
      case SHORTCUT_KEYS.STARRED_RESOURCES: {
        return {
          to: {
            name: "StarredResource",
          },
        };
      }
      case SHORTCUT_KEYS.SHARED_WITH_ME: {
        return {
          to: {
            name: "MyDriveSharedWithMe",
          },
        };
      }
      case SHORTCUT_KEYS.COMMUNITIES: {
        return {
          to: {
            name: "CommunityListing",
          },
        };
      }
      case SHORTCUT_KEYS.ASSIGNMENTS: {
        return {
          to: {
            name: "Assignment",
          },
        };
      }
      case SHORTCUT_KEYS.ASSESSMENTS: {
        return {
          to: {
            name: "Assessment",
          },
        };
      }
      case SHORTCUT_KEYS.PLANNER: {
        return {
          to: {
            name: "Planner",
          },
        };
      }
      case SHORTCUT_KEYS.MANAGE_STUDENT_PASSWORD_OR_PASSCODE: {
        return {
          to: {
            name: "PasswordResetAndPasscodeGenerate",
          },
        };
      }
      case SHORTCUT_KEYS.MY_SUBJECTS: {
        return {
          to: {
            name: "MySubjects",
          },
        };
      }
      case SHORTCUT_KEYS.SELF_STUDY: {
        return {
          to: {
            name: "SelfStudy",
          },
        };
      }
      case SHORTCUT_KEYS.ALS: {
        return {
          to: {
            name: "AlsLandingPage",
          },
        };
      }
      case SHORTCUT_KEYS.ACCOUNT: {
        return {
          to: {
            name: "ProfileSummary",
          },
        };
      }
      case SHORTCUT_KEYS.ADMIN: {
        return {
          to: {
            name: "Admin",
          },
        };
      }
      case SHORTCUT_KEYS.NOTEBOOK: {
        return {
          to: {
            name: "Notebook",
          },
        };
      }
      case SHORTCUT_KEYS.USER_RECENTLY_ACCESSED: {
        return {
          to: {
            name: "UserRecentlyAccessed",
          },
        };
      }
      case SHORTCUT_KEYS.HOME: {
        return {
          to: {
            name: "Home",
          },
        };
      }
      case SHORTCUT_KEYS.GROUPS: {
        return {
          to: {
            name: "StudentGroupListing",
          },
        };
      }
      default:
        return null;
    }
  };

  const handleEventDefaultAndCallbacks = (callback?: () => void, e?: Event) => {
    callback?.();
    if (isDefined(e)) {
      e?.stopPropagation();
      e?.preventDefault();
    }
  };

  const getCategoryTitle = (category: Category): string => {
    switch (category) {
      case CATEGORIES.PERSONAL_WORKSPACE:
        return t("app_side_nav_view_menu_subpage_personal_workspace_title");
      case CATEGORIES.CONTENT_AND_RESOURCES:
        return t("app_side_nav_view_menu_subpage_content_and_resources_title");
      case CATEGORIES.TEACHING_AND_ASSESSMENTS:
        return t("app_side_nav_view_menu_subpage_teaching_and_assessments_title");
      case CATEGORIES.GROUPS_AND_COLLABORATION:
        return t("app_side_nav_view_menu_subpage_groups_and_collaboration_title");
      case CATEGORIES.STUDY_TOOLS_AND_RESOURCES:
        return t("app_side_nav_view_menu_subpage_study_tools_and_resources_title");
      case CATEGORIES.ACCOUNT_MANAGEMENT:
        return t("app_side_nav_view_menu_subpage_account_management_title");
      default:
        return "";
    }
  };

  return {
    getAvailableShortcuts,
    getShortcutItems,
    getEventBinding,
    getLinkBinding,
    getDisplayedShortcutKeysByCategory,
    getCategoryTitle,
  };
}
